/* eslint-disable react/jsx-props-no-spreading */
import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import map from 'lodash/map';
import NoIndex from 'components/global/NoIndex';
import PreAuthTopBar from 'components/global/PreAuthTopBar';
// eslint-disable-next-line import/no-cycle
import Panel from '../onboarding/Panel';
import PersonalDetails from '../onboarding/pages/PersonalDetails';
import { fetchUser } from '../../actions/user';
import { fetchStates, fetchPronouns } from '../../actions/misc';
import LoadingPanel from '../global/LoadingPanel';
import PracticeDetails from '../onboarding/pages/PracticeDetails0';
import ThankYou from '../onboarding/pages/ThankYou';
import { scrollToTop } from '../onboarding/scroll';
import TaxInformation from '../onboarding/pages/TaxInformation';
import { COUNTRY } from '../../environment';
import ErrorDialog from '../onboarding/ErrorDialog';
import {
  PERSONAL_DETAILS,
  PRACTICE_DETAILS,
  LOCATION_DETAILS,
  MATCHING_PREFERENCES,
  THANK_YOU,
  TAX_INFORMATION,
} from './data';
import { signoutUser } from '../../actions/auths';
import LocationDetails from '../onboarding/pages/LocationDetails';
import MatchingPreferences from '../onboarding/pages/MatchingPreferences';

function PracticeOnboarding({
  ageGroups = [],
  authenticated = false,
  designations = [],
  disorders = [],
  history,
  languages = [],
  fetchUser,
  insuranceCompanies = [],
  approachesToCare = [],
  signoutUser,
  fetchPronouns,
  fetchStates,
}) {
  const intl = useIntl();

  const [isClinicCounsellor, setIsClinicCounsellor] = useState(false);
  const [page, setPage] = useState(PERSONAL_DETAILS);
  const [subPage, setSubPage] = useState(0);
  const [loadingPanel, setLoadingPanel] = useState({
    open: false,
    message: '',
  });
  const [error, setError] = useState({ open: false, message: '' });

  // componentDidMount equivalent
  useEffect(() => {
    fetchStates(COUNTRY === 'CA' ? 'CAN' : 'USA');
    fetchPronouns();
    if (authenticated) {
      redirect();
    }
  }, [authenticated, fetchStates, fetchPronouns]);

  // scrollToTop when subPage or page changes
  useEffect(() => {
    scrollToTop();
  }, [subPage, page]);

  const nextSubPage = () => setSubPage(prev => prev + 1);
  const previousSubPage = () => setSubPage(prev => prev - 1);

  const nextPage = () => setPage(prev => prev + 1);
  const previousPage = () => setPage(prev => prev - 1);

  const openErrorDialog = message => setError({ open: true, message });
  const closeErrorDialog = () => setError({ open: false, message: '' });

  const openLoadingPanel = message => {
    scrollToTop();
    setLoadingPanel({ open: true, message });
  };
  const closeLoadingPanel = () => setLoadingPanel({ open: false, message: '' });

  const skipToEnd = () => setPage(THANK_YOU);
  const skipToNext = currentStep => setPage(currentStep + 1);

  const sendToDashboard = () => {
    fetchUser(() => history.push('/dashboard'));
  };

  const errorCallback = (_error, message) => {
    closeLoadingPanel();
    openErrorDialog(message);
  };

  const redirect = () => {
    openLoadingPanel(intl.formatMessage({ defaultMessage: 'Loading...' }));
    fetchUser(
      response => {
        closeLoadingPanel();
        if (response.data) {
          setPage(PRACTICE_DETAILS);
        }
      },
      () => {
        signoutUser();
        history.push('/signin');
      },
    );
  };

  const pageProps = {
    currentStep: page,
    nextPage,
    previousPage,
    openErrorDialog,
    openLoadingPanel,
    closeLoadingPanel,
    errorCallback,
    skipToEnd,
    skipToNext,
  };

  return (
    <>
      <NoIndex />
      <PreAuthTopBar locale={localStorage.getItem('locale')} />
      <div className="practitioner-onboarding-layout">
        <div className="light-background" />
        <div className="dark-background" />
        <Panel
          isClinicCounsellor={isClinicCounsellor}
          currentStep={page}
          specialized
        >
          {page === PERSONAL_DETAILS && (
            <PersonalDetails {...pageProps} specialized />
          )}
          {page === PRACTICE_DETAILS && COUNTRY === 'CA' && (
            <PracticeDetails
              isClinicCounsellor={isClinicCounsellor}
              setIsClinicCounsellor={setIsClinicCounsellor}
              specialized
              nextPage={nextPage}
              skipToEnd={skipToEnd}
              {...pageProps}
            />
          )}
          {page === LOCATION_DETAILS && (
            <LocationDetails {...pageProps} nextPage={nextPage} specialized />
          )}
          {page === MATCHING_PREFERENCES && (
            <MatchingPreferences
              currentStep={page}
              isClinicCounsellor={isClinicCounsellor}
              setPage={setPage}
              skipToEnd={skipToEnd}
              specialized
              {...pageProps}
            />
          )}
          {page === TAX_INFORMATION && COUNTRY === 'CA' && (
            <TaxInformation {...pageProps} skipToEnd={skipToEnd} specialized />
          )}
          {page === THANK_YOU && (
            <ThankYou
              currentStep={page}
              nextPage={sendToDashboard}
              nonFormPage
              specialized
            />
          )}
          {loadingPanel.open && <LoadingPanel message={loadingPanel.message} />}
          <ErrorDialog
            open={error.open}
            handleClose={closeErrorDialog}
            message={error.message}
          />
        </Panel>
      </div>
    </>
  );
}

PracticeOnboarding.propTypes = {
  ageGroups: PropTypes.array,
  authenticated: PropTypes.bool,
  designations: PropTypes.array,
  disorders: PropTypes.array,
  history: PropTypes.object.isRequired,
  languages: PropTypes.array,
  fetchUser: PropTypes.func.isRequired,
  insuranceCompanies: PropTypes.array,
  approachesToCare: PropTypes.array,
  signoutUser: PropTypes.func.isRequired,
  fetchPronouns: PropTypes.func.isRequired,
  fetchStates: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  ageGroups: state.misc.ageGroups,
  authenticated: state.auth.authenticated,
  designations: map(state.misc.designations, designation => ({
    value: designation.acronym,
    label: designation.name,
  })),
  disorders: state.misc.disorders,
  languages: state.misc.languages,
  insuranceCompanies: state.misc.insuranceCompanies,
  approachesToCare: map(state.misc.approachesToCare, approach => ({
    value: approach.locale_key,
    label: approach.name,
  })),
});

export default connect(mapStateToProps, {
  signoutUser,
  fetchUser,
  fetchPronouns,
  fetchStates,
})(PracticeOnboarding);
